import * as I18n from 'i18n-js';
import BankAccountFields from './bank_account_fields';
import * as React from 'react';
import ErrorMessage from './payout_method_error_message';
import bind from '@reverbdotcom/commons/dist/bind';
import SanitizedRender from '@reverbdotcom/commons/dist/components/sanitized_render';
import { ReverbPaymentsPayoutMethod } from './payment_provider';

interface DirectCheckoutProfileAttributes {
  nameOnAccount: string;
  maskedNameSummary: string;
  bankName: string;
  countryCode: string;
  payoutMethod: string;
  hasAcceptedReverbBankTerms: boolean;
  profileErrors: string[];
  currency: string;
}

export interface Props {
  profileAttributes: DirectCheckoutProfileAttributes;
  hideBankAccountEditForm: boolean;
  dcProfileComplete: boolean;
  preferredSeller: boolean;
  hasCompletedPayouts: boolean;
  formPrefix: string;
  shopCurrency: string;
  bankChangesLocked: boolean;
  accountRepName: string;
  accountRepEmail: string;
  onCountryChange(countryCode: string): void;
  onCurrencyChange(currencyCode: string): void;
}

interface State {
  isEditing: boolean;
  selectedPayoutCurrency: string;
}

const REVERB_PAYMENTS_HELP_ARTICLE = 'https://help.reverb.com/hc/en-us/articles/360002778613-How-to-Get-Paid-with-Reverb-Payments-';

export default class InternalPayoutsFormFields extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isEditing: this.hasDirectCheckoutProfileErrors || !this.props.hideBankAccountEditForm,
      selectedPayoutCurrency: this.props.profileAttributes.currency,
    };
  }

  nameForField(fieldName) {
    return `${this.props.formPrefix}[${fieldName}]`;
  }

  idForField(fieldName) {
    return `${this.props.formPrefix}-${fieldName}`;
  }

  get achPayoutDescriptionText() {
    if (this.props.preferredSeller) {
      return I18n.t('discovery.payoutMethod.directCheckoutProfile.achPayoutDescription.preferredSellerPayOutTimingMessage');
    }
    if (this.props.hasCompletedPayouts) {
      return I18n.t('discovery.payoutMethod.directCheckoutProfile.achPayoutDescription.payOutTimingMessageHtml', { linkOpen: `<a href='${REVERB_PAYMENTS_HELP_ARTICLE}' rel='noreferrer noopener' target='_blank'>`, linkClose: '</a>' });
    }
    return I18n.t('discovery.payoutMethod.directCheckoutProfile.achPayoutDescription.firstSalePayOutTimingMessageHtml', { linkOpen: `<a href='${REVERB_PAYMENTS_HELP_ARTICLE}' rel='noreferrer noopener' target='_blank'>`, linkClose: '</a>' });
  }

  @bind
  updateSelectedPayoutCurrency(selectedPayoutCurrency) {
    this.setState({ selectedPayoutCurrency });
    this.props.onCurrencyChange(selectedPayoutCurrency);
  }

  bankAccountForm() {
    return (
      <div>
        {this.nevermindLink}
        <div>
          <p className="strong mb-0">
            {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankInfo')}
            <SecurelyStoredDataCallout />
          </p>
          <p className="hint mb-1 mobile-mt-1 mobile-mb-2">
            {I18n.t('discovery.payoutMethod.directCheckoutProfile.weNeedYourBankInfo')}
          </p>
          <input
            value={ReverbPaymentsPayoutMethod.ACH}
            type="hidden"
            name={this.nameForField('payout_method')}
          />
          <BankAccountFields
            inputNamePrefix={this.props.formPrefix}
            countryCode={this.props.profileAttributes.countryCode}
            onCountryChange={this.props.onCountryChange}
            selectedPayoutCurrency={this.state.selectedPayoutCurrency}
            onCurrencyChange={this.updateSelectedPayoutCurrency}
            shopCurrency={this.props.shopCurrency}
          />
        </div>
      </div>
    );
  }

  get bankName() {
    const name = this.props.profileAttributes.bankName;
    if (name) {
      return <p className="mb-0">{name}</p>;
    }
    return null;
  }

  bankSummary() {
    return (
      <div className="form-group mt-0 mb-2" data-readonly-profile-fields>
        <div className="form-group__header">
          <label>
            {I18n.t('discovery.payoutMethod.directCheckoutProfile.bankInfo')}
          </label>
          <SecurelyStoredDataCallout />
        </div>
        <div className="form-group__fields">
          <p className="mb-0">{this.props.profileAttributes.nameOnAccount}</p>
          <p className="mb-0">{this.props.profileAttributes.maskedNameSummary}</p>
          {this.bankName}
          <p className="mt-half mb-1">{this.useADifferentAccountLink}</p>
        </div>
      </div>
    );
  }

  toggleEditing() {
    this.setState(prevState => ({ isEditing: !prevState.isEditing }));
  }

  get useADifferentAccountLink() {
    if (this.props.bankChangesLocked) {
      const accountRepName = this.props.accountRepName ? this.props.accountRepName : 'Reverb';
      const accountRepEmail = this.props.accountRepEmail ? this.props.accountRepEmail : 'sellerengagement@reverb.com';
      return (
        <SanitizedRender
          htmlTag="span"
          id="bankChangeHelpText"
          className="weight-bold"
          html={
            I18n.t('discovery.payoutMethod.directCheckoutProfile.bankChangesLockedHtml', {
              icon: this.lockIcon,
              linkOpen: `<a href="mailto:${accountRepEmail}?subject=Account%20Unlock%20Request">`,
              name: accountRepName,
              linkClose: '</a>',
            })
          }
        />
      );
    }
    const text = I18n.t('discovery.payoutMethod.directCheckoutProfile.useADifferentAccountHtml', { icon: this.editIcon });
    return (
      <a
        data-change-link
        dangerouslySetInnerHTML={{ __html: text }}
        onClick={() => {
          this.toggleEditing();
        }}
      />
    );
  }

  get nevermindLink() {
    if (!this.props.hideBankAccountEditForm || !this.props.dcProfileComplete) {
      return false;
    }

    return (
      <p className="mb-1">
        <a data-nevermind-link onClick={() => { this.toggleEditing(); }}>
          {I18n.t('discovery.payoutMethod.directCheckoutProfile.nevermind')}
        </a>
      </p>
    );
  }

  get editIcon() {
    return '<span class="fa fa-pencil"></span>';
  }

  get lockIcon() {
    return '<span class="fa fa-lock"></span>';
  }

  get hasDirectCheckoutProfileErrors() {
    return (this.props.profileAttributes.profileErrors.length > 0);
  }

  get joinedDirectCheckoutProfileErrors() {
    return (this.props.profileAttributes.profileErrors.join(' '));
  }

  get showCollapsedAchSummary() {
    const { isEditing } = this.state;
    const { dcProfileComplete, bankChangesLocked } = this.props;

    return (
      (!isEditing && dcProfileComplete) ||
        (!dcProfileComplete && bankChangesLocked)
    );
  }

  renderAchProfile() {
    if (this.showCollapsedAchSummary) {
      return this.bankSummary();
    }
    return this.bankAccountForm();
  }

  render() {
    return (
      <div>
        {this.hasDirectCheckoutProfileErrors &&
          <ErrorMessage
            message={this.joinedDirectCheckoutProfileErrors}
            color="red"
            small
          />
        }
        <p className="weight-bold mb-0">
          {I18n.t('discovery.payoutMethod.directCheckoutProfile.achPayoutDescription.setUpForAch')}
        </p>
        <p dangerouslySetInnerHTML={{ __html: this.achPayoutDescriptionText }} />
        {this.renderAchProfile()}
      </div>
    );
  }
}

function SecurelyStoredDataCallout() {
  return (
    <span className="ml-space small muted">
      <span className="fa fa-lock mr-space" />
      {I18n.t('discovery.payoutMethod.directCheckoutProfile.storedSecurelyAndEncrypted')}
    </span>
  );
}



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/discovery-ui/src/components/internal_payouts_form_fields.tsx